import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

/**
 * Scheda stampabile per SMALTIMENTO / RICONSEGNA pneumatici.
 * Layout identico al modulo cartaceo Pneumatici Centro Vasto / SP Car Service Cupello.
 * Renderizza DUE copie identiche sulla stessa pagina A4 (cliente + officina).
 * Per salvare in PDF: usa "Stampa" del browser → "Salva come PDF".
 */
export default function Scheda() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deposit, setDeposit] = useState(null);

  useEffect(() => {
    api.get(`/deposits/${id}`).then(({ data }) => setDeposit(data)).catch(() => {});
  }, [id]);

  if (!deposit) {
    return <div className="p-8 text-center font-bold uppercase" data-testid="scheda-loading">Caricamento scheda...</div>;
  }

  const c = deposit.cliente || {};
  const isSmaltimento = deposit.stato === "Smaltito" || !!deposit.numero_scheda_smaltimento;
  const isRiconsegna = deposit.stato === "Ritirato" || !!deposit.numero_scheda_riconsegna;
  const prog = deposit.numero_scheda_smaltimento || deposit.numero_scheda_riconsegna || deposit.numero_scheda || "";

  const dataObj = deposit.data_lavorazione || deposit.data_intervento || "";
  const [yyyy, mm, dd] = (dataObj || "").split("-");

  return (
    <>
      <div className="print:hidden max-w-3xl mx-auto p-4 flex gap-2" data-testid="scheda-toolbar">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="border-2 border-black bg-white px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Indietro
        </button>
        <button
          onClick={() => window.print()}
          data-testid="print-scheda-btn"
          className="bg-[#E60000] text-white border-2 border-black px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-black inline-flex items-center gap-2"
        >
          <Printer className="w-4 h-4" strokeWidth={3} /> Stampa / Salva PDF
        </button>
        <div className="text-xs opacity-70 flex items-center ml-2">
          Consiglio: nella finestra di stampa scegli <strong className="mx-1">Salva come PDF</strong> per esportare, oppure lascia stampante.
        </div>
      </div>

      <div className="scheda-container" data-testid="scheda-print">
        <SchedaCopia deposit={deposit} c={c} isSmaltimento={isSmaltimento} isRiconsegna={isRiconsegna} prog={prog} dd={dd} mm={mm} yyyy={yyyy} />
        <div className="cut-line"><span>✂ - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -</span></div>
        <SchedaCopia deposit={deposit} c={c} isSmaltimento={isSmaltimento} isRiconsegna={isRiconsegna} prog={prog} dd={dd} mm={mm} yyyy={yyyy} />
      </div>

      <style>{`
        .scheda-container {
          max-width: 780px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Manrope', sans-serif;
          color: #000;
          background: #fff;
        }
        .scheda-copia {
          padding: 8px 20px 12px;
          border: 1px solid transparent;
        }
        .scheda-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
          border-bottom: 2px solid #000;
          padding-bottom: 10px;
        }
        .scheda-brand {
          flex: 1;
        }
        .scheda-brand h1 {
          font-family: 'Chivo', sans-serif;
          font-weight: 900;
          font-size: 20px;
          letter-spacing: -0.02em;
          margin: 0;
          line-height: 1.1;
          color: #000;
        }
        .scheda-brand .amp {
          text-align: center;
          font-weight: 900;
          font-size: 18px;
          margin: 2px 0;
        }
        .scheda-brand h2 {
          font-family: 'Chivo', sans-serif;
          font-weight: 900;
          font-size: 20px;
          margin: 0;
          line-height: 1.1;
          color: #E60000;
        }
        .scheda-brand .services {
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 6px;
        }
        .scheda-brand .services strong { color: #E60000; }
        .scheda-logo {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
        }
        .scheda-logo .pn {
          font-family: 'Chivo', sans-serif;
          font-weight: 900;
          font-size: 14px;
        }
        .scheda-logo .cv {
          font-family: 'Chivo', sans-serif;
          font-style: italic;
          font-size: 18px;
          color: #E60000;
          font-weight: 900;
        }
        .scheda-body {
          padding-top: 12px;
          font-size: 12px;
        }
        .scheda-prog {
          text-align: right;
          font-size: 13px;
          margin-bottom: 8px;
        }
        .scheda-prog b { border-bottom: 1px solid #000; padding: 0 30px; font-family: 'JetBrains Mono', monospace; }
        .row {
          margin: 8px 0;
          display: flex;
          align-items: baseline;
          gap: 6px;
        }
        .row label {
          font-weight: 700;
        }
        .row .fill {
          flex: 1;
          border-bottom: 1px solid #000;
          padding: 0 6px 2px;
          min-height: 16px;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 700;
        }
        .row-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        .row-3 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
        }
        .authorize {
          margin-top: 10px;
          font-size: 12px;
        }
        .checkbox-row {
          display: grid;
          grid-template-columns: 220px 1fr 1fr;
          gap: 10px;
          align-items: center;
          margin: 6px 0;
        }
        .cb-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cb {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1.5px solid #000;
          position: relative;
        }
        .cb.checked::after {
          content: "✔";
          position: absolute;
          top: -4px;
          left: 1px;
          font-weight: 900;
          font-size: 14px;
          color: #E60000;
        }
        .cb-label { font-weight: 700; letter-spacing: 0.03em; }
        .cut-line {
          text-align: center;
          font-family: monospace;
          color: #666;
          margin: 4px 0;
          font-size: 12px;
          user-select: none;
        }
        .cut-line span { padding: 0 6px; }
        .foot {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 20px;
          margin-top: 14px;
          font-size: 12px;
        }
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: #fff !important; }
          header, footer, nav, .print\\:hidden { display: none !important; }
          .scheda-container { padding: 0; max-width: 100%; }
          .scheda-copia { break-inside: avoid; }
          .cut-line { color: #999; }
        }
      `}</style>
    </>
  );
}

function SchedaCopia({ deposit, c, isSmaltimento, isRiconsegna, prog, dd, mm, yyyy }) {
  return (
    <div className="scheda-copia">
      <div className="scheda-header">
        <div className="scheda-brand">
          <h1>PNEUMATICI CENTRO VASTO</h1>
          <div className="amp">&amp;</div>
          <h2>SP CAR SERVICE CUPELLO</h2>
          <div className="services">
            VENDITA E ASSISTENZA PNEUMATICI<br />
            OFFICINA MECCANICA <strong>|</strong> CENTRO REVISIONI <strong>|</strong> CARROZZERIA <strong>|</strong> SOCCORSO STRADALE
          </div>
        </div>
        <div className="scheda-logo">
          <div className="pn">Pneumatici</div>
          <div className="cv">Centro Vasto</div>
          <div style={{ marginTop: 8 }}>
            Via Martiri della Libertà, 54<br />
            66054 VASTO (CH) — Tel. 0873 374025<br />
            La Rampa — P.IVA 02389500699<br />
            www.pneumaticicentrovasto.com<br />
            info@pcv.shop
          </div>
        </div>
      </div>

      <div className="scheda-body">
        <div className="scheda-prog">Prog. <b>{prog || "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0"}</b></div>

        <div className="row">
          <label>Il sottoscritto:</label>
          <div className="fill">{c.nome || ""}</div>
        </div>
        <div className="row">
          <label>proprietario del veicolo:</label>
          <div className="fill">{c.nome || ""}</div>
        </div>

        <div className="row-2">
          <div className="row"><label>Marca</label><div className="fill">{deposit.marca_veicolo || ""}</div></div>
          <div className="row"><label>Modello</label><div className="fill">{deposit.modello_veicolo || ""}</div></div>
        </div>

        <div className="row" style={{ maxWidth: 320 }}>
          <label>Targa</label>
          <div className="fill">{deposit.targa || ""}</div>
        </div>

        <div className="authorize">
          <div>con la presente autorizza la Ditta <strong>PNEUMATICI CENTRO VASTO</strong></div>
          <div style={{ marginTop: 4 }}>a:</div>

          <div className="checkbox-row">
            <div className="cb-item">
              <span className={`cb ${isSmaltimento ? "checked" : ""}`}></span>
              <span className="cb-label">SMALTIMENTO</span>
            </div>
            <div style={{ fontWeight: 700 }}>dei seguenti</div>
            <div className="cb-item">
              <span className="cb checked"></span>
              <span className="cb-label">pneumatici v/v depositati:</span>
            </div>
          </div>
          <div className="checkbox-row">
            <div className="cb-item">
              <span className={`cb ${isRiconsegna ? "checked" : ""}`}></span>
              <span className="cb-label">RICONSEGNA.</span>
            </div>
            <div></div>
            <div></div>
          </div>
        </div>

        <div className="row-3" style={{ marginTop: 10 }}>
          <div className="row"><label>Misura</label><div className="fill">{deposit.misura || ""}</div></div>
          <div className="row"><label>Marca</label><div className="fill">{deposit.marca_pneumatico || ""}</div></div>
          <div className="row"><label>Modello</label><div className="fill">{deposit.modello_pneumatico || ""}</div></div>
        </div>

        <div className="foot">
          <div className="row">
            <label>Data:</label>
            <div className="fill" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {dd ? `${dd} / ${mm} / ${yyyy}` : ""}
            </div>
          </div>
          <div className="row">
            <label>Firma:</label>
            <div className="fill"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
